// src/pages/MaryGuardianPage.jsx
import {
  ArrowUpRight,
  CheckCircle2,
  Cpu,
  FileText,
  GitBranch,
  HardDrive,
  Lock,
  Mail,
  MessageSquare,
  Moon,
  Phone,
  Scale,
  Search,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  Terminal,
  Zap,
  Database
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnimatedBackground from '../components/AnimatedBackground';
import Footer from '../components/Footer';
import RequestModal from '../components/RequestModal';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner'; // Не забудьте импорт

const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => setIsVisible(entry.isIntersecting));
    });
    observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`transition-all duration-700 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
      ref={domRef}
    >
      {children}
    </div>
  );
};

const PricingCard = ({ name, price, period, features, isPopular, ctaText, onSelect }) => (
  <div className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 ${
    isPopular 
      ? 'bg-gradient-to-b from-primary to-secondary text-white shadow-2xl scale-105 md:scale-110' 
      : 'backdrop-blur-sm bg-bg/40 border border-fg/10 hover:shadow-xl'
  }`}>
    {isPopular && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="px-4 py-1 bg-accent text-white text-sm font-bold rounded-full">
          POPULAR
        </span>
      </div>
    )}
    <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-fg'}`}>{name}</h3>
    <div className="mb-6">
      <span className={`text-5xl font-bold ${isPopular ? 'text-white' : 'text-fg'}`}>${price}</span>
      <span className={`${isPopular ? 'text-white/70' : 'text-fg/50'}`}>{period}</span>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2 text-sm">
          <CheckCircle2 size={16} className={isPopular ? 'text-white/70' : 'text-primary'} />
          <span className={isPopular ? 'text-white/80' : 'text-fg/60'}>{feature}</span>
        </li>
      ))}
    </ul>
    <button 
      onClick={() => onSelect(name, price)}
      className={`w-full py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
        isPopular 
          ? 'bg-white text-primary hover:shadow-lg' 
          : 'bg-gradient-to-r from-primary to-secondary text-white hover:shadow-lg'
      }`}
    >
      {ctaText}
    </button>
  </div>
);

const MaryGuardianPage = ({ darkMode, setDarkMode, changeLanguage }) => {
  const { t } = useTranslation();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({ name: '', price: '' });

  const handlePlanSelect = (planName, planPrice) => {
    setSelectedPlan({ name: planName, price: planPrice });
    setIsModalOpen(true);
  };

  const handleStart = () => {
    setSelectedPlan({ name: 'Alpha', price: '9' });
    setIsModalOpen(true);
  };

  return (
    <>
      <SEO 
        title="Mary Guardian | Local AI Agent Orchestration (2B Q4)"
        description="Run powerful AI agents locally on 8GB RAM. Document comparison, dispute drafting, and data extraction using orchestrated 2B quantized models."
        path="/mary-guardian"
      />

      <AnimatedBackground darkMode={darkMode} />
      <div className="min-h-screen relative z-10 text-fg transition-colors duration-500">
        
        {/* Header ... (оставляем как есть) */}
        <header className="fixed top-0 left-0 right-0 w-full z-50 bg-bg/80 backdrop-blur-md shadow-sm border-b border-fg/10">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="hidden md:flex text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent hover:opacity-80 transition-opacity cursor-pointer group items-center gap-2">
              COMBI<span className="text-fg/50">.kz</span>
              <Sparkles size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
            </Link>
            
            <div className="md:hidden"></div>
            
            <div className="flex gap-2 md:gap-4 items-center">
              <Link to="/" className="px-2 md:px-4 py-1 md:py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg hover:shadow-lg transition-all hover:scale-105 text-xs md:text-sm font-medium whitespace-nowrap">
                {t('automation')}
              </Link>
              
              <div className="flex gap-1 md:gap-2 items-center">
                <button onClick={() => changeLanguage('ru')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-ru text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('en')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-gb text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('kk')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-kz text-sm md:text-base"></span>
                </button>
                <button onClick={() => setDarkMode(!darkMode)} className="p-1 md:p-2 rounded-lg bg-fg/10 backdrop-blur-sm hover:bg-fg/20 transition">
                  {darkMode ? <Sun size={16} className="md:w-5 md:h-5 text-accent" /> : <Moon size={16} className="md:w-5 md:h-5 text-fg/70" />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="pt-20">
          {/* Hero Section */}
          <FadeInSection delay={0}>
            <section className="container mx-auto px-6 py-20">
              <div className="text-center max-w-4xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm text-primary px-4 py-2 rounded-full border border-primary/20 mb-6">
                  <HardDrive size={16} />
                  <span className="text-sm font-medium">{t('guardianBadge')}</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  {t('guardianHeroTitle1')}
                  <br />
                  <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    {t('guardianHeroTitle2')}
                  </span>
                </h1>
                <p className="text-xl text-fg/60 mb-8 max-w-2xl mx-auto">
                  {t('guardianHeroDesc')}
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <button 
                    onClick={handleStart}
                    className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl hover:shadow-xl transition-all hover:scale-105 text-lg font-semibold"
                  >
                    {t('guardianCtaPrimary')}
                    <ArrowUpRight size={20} />
                  </button>
                  <button 
                    onClick={() => handlePlanSelect('Enterprise', '1999')}
                    className="flex items-center gap-2 px-8 py-4 bg-fg/80 backdrop-blur-sm text-primary rounded-xl hover:bg-fg transition border border-bg/10 text-lg"
                  >
                    {t('guardianCtaSecondary')}
                  </button>
                </div>
                <p className="mt-6 text-sm text-fg/40">{t('guardianAlphaNote')}</p>
              </div>
            </section>
          </FadeInSection>

          {/* NEW SECTION: The Power of Small Models & Orchestration */}
          <FadeInSection delay={100}>
            <section className="container mx-auto px-6 py-16">
              <div className="backdrop-blur-sm bg-bg/40 dark:bg-fg/10 border border-fg/10 rounded-3xl p-8 md:p-12 shadow-xl">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-mono mb-4">
                      <Terminal size={14} />
                      <span>Technical Deep Dive</span>
                    </div>
                    <h2 className="text-3xl font-bold mb-4">{t('orchestrationTitle')}</h2>
                    <p className="text-fg/70 mb-6 leading-relaxed">
                      {t('orchestrationDesc')}
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-primary mt-1" />
                        <div>
                          <strong className="text-fg">{t('techPoint1Title')}</strong>
                          <p className="text-sm text-fg/60">{t('techPoint1Desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-primary mt-1" />
                        <div>
                          <strong className="text-fg">{t('techPoint2Title')}</strong>
                          <p className="text-sm text-fg/60">{t('techPoint2Desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-primary mt-1" />
                        <div>
                          <strong className="text-fg">{t('techPoint3Title')}</strong>
                          <p className="text-sm text-fg/60">{t('techPoint3Desc')}</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 blur-2xl opacity-20 rounded-full"></div>
                    <div className="relative bg-black/50 rounded-xl p-6 font-mono text-sm text-green-400 border border-fg/10 shadow-2xl overflow-hidden">
                      <div className="flex gap-2 mb-4 border-b border-fg/10 pb-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <p className="mb-2"><span className="text-blue-400">$</span> loading model: llama-3-2b-q4.gguf</p>
                      <p className="mb-2 text-fg/50">// VRAM Usage: 1.8GB / 8.0GB</p>
                      <p className="mb-2"><span className="text-blue-400">$</span> ingesting: contract_v1.pdf, invoice_v2.pdf</p>
                      <p className="mb-2 text-yellow-400">// Extracting entities... Done.</p>
                      <p className="mb-2"><span className="text-blue-400">$</span> comparing documents...</p>
                      <p className="mb-2 text-purple-400">// Discrepancy found: Clause 4.2 mismatch.</p>
                      <p className="mb-2"><span className="text-blue-400">$</span> generating dispute letter...</p>
                      <p className="animate-pulse">_</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </FadeInSection>

          {/* Features Section (Updated icons/text logic if needed) */}
          <FadeInSection delay={200}>
            <section className="container mx-auto px-6 py-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                {t('guardianFeaturesTitle')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: Cpu, titleKey: "guardianFeat1Title", descKey: "guardianFeat1Desc" },
                  { icon: Database, titleKey: "guardianFeatCompareTitle", descKey: "guardianFeatCompareDesc" }, // New feature
                  { icon: Search, titleKey: "guardianFeat3Title", descKey: "guardianFeat3Desc" },
                  { icon: Scale, titleKey: "guardianFeat4Title", descKey: "guardianFeat4Desc" },
                  { icon: GitBranch, titleKey: "guardianFeat5Title", descKey: "guardianFeat5Desc" },
                  { icon: Lock, titleKey: "guardianFeat6Title", descKey: "guardianFeat6Desc" }
                ].map((item, idx) => (
                  <div key={idx} className="backdrop-blur-sm bg-primary/5 border border-primary/10 rounded-xl p-6">
                    <item.icon size={40} className="text-primary mb-4" />
                    <h3 className="text-lg font-semibold mb-2 text-fg">{t(item.titleKey)}</h3>
                    <p className="text-fg/60">{t(item.descKey)}</p>
                  </div>
                ))}
              </div>
            </section>
          </FadeInSection>

          {/* Architecture / How it works */}
          <FadeInSection delay={300}>
            <section className="container mx-auto px-6 py-16 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-3xl my-8">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                {t('guardianArchTitle')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-6xl mx-auto items-start">
                {[
                  { step: "1", titleKey: "guardianArch1Title", descKey: "guardianArch1Desc", icon: FileText },
                  { step: "2", titleKey: "guardianArch2Title", descKey: "guardianArch2Desc", icon: Search },
                  { step: "3", titleKey: "guardianArch3Title", descKey: "guardianArch3Desc", icon: Shield },
                  { step: "4", titleKey: "guardianArch4Title", descKey: "guardianArch4Desc", icon: Terminal },
                  { step: "5", titleKey: "guardianArch5Title", descKey: "guardianArch5Desc", icon: Scale }
                ].map((item, idx) => (
                  <div key={idx} className="text-center relative">
                    <div className="w-14 h-14 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white mx-auto mb-4">
                      <item.icon size={24} />
                    </div>
                    <h3 className="text-lg font-semibold mb-1 text-fg">{t(item.titleKey)}</h3>
                    <p className="text-fg/60 text-sm">{t(item.descKey)}</p>
                    {idx < 4 && (
                      <div className="hidden md:block absolute top-7 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-primary/30 to-transparent" />
                    )}
                  </div>
                ))}
              </div>
            </section>
          </FadeInSection>

          {/* Pricing Section */}
          <FadeInSection delay={400}>
            <section className="container mx-auto px-6 py-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                {t('guardianPricingTitle')}
              </h2>
              <p className="text-center text-fg/60 mb-12 max-w-2xl mx-auto">
                {t('guardianPricingSubtitle')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
                <PricingCard
                  name={t('guardianLiteName')}
                  price="29"
                  period={t('guardianLitePeriod')}
                  features={[
                    t('guardianLiteFeat1'),
                    t('guardianLiteFeat2'),
                    t('guardianLiteFeat3'),
                    t('guardianLiteFeat4'),
                    t('guardianLiteFeat5')
                  ]}
                  ctaText={t('guardianLiteCta')}
                  onSelect={handlePlanSelect}
                />
                <PricingCard
                  name={t('guardianBusinessName')}
                  price="99"
                  period={t('guardianBusinessPeriod')}
                  features={[
                    t('guardianBusinessFeat1'),
                    t('guardianBusinessFeat2'),
                    t('guardianBusinessFeat3'),
                    t('guardianBusinessFeat4'),
                    t('guardianBusinessFeat5')
                  ]}
                  isPopular={true}
                  ctaText={t('guardianBusinessCta')}
                  onSelect={handlePlanSelect}
                />
                <PricingCard
                  name={t('guardianEnterpriseName')}
                  price="299"
                  period={t('guardianEnterprisePeriod')}
                  features={[
                    t('guardianEnterpriseFeat1'),
                    t('guardianEnterpriseFeat2'),
                    t('guardianEnterpriseFeat3'),
                    t('guardianEnterpriseFeat4'),
                    t('guardianEnterpriseFeat5')
                  ]}
                  ctaText={t('guardianEnterpriseCta')}
                  onSelect={handlePlanSelect}
                />
              </div>
              <div className="text-center mt-8">
                <p className="text-fg/50 text-sm max-w-3xl mx-auto">
                  {t('guardianPricingNote')}
                </p>
              </div>
            </section>
          </FadeInSection>

          {/* Tech Specs */}
          <FadeInSection delay={500}>
            <section className="container mx-auto px-6 py-16">
              <div className="backdrop-blur-sm bg-gradient-to-r from-fg/5 to-fg/10 rounded-2xl p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  {t('guardianTechTitle')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold text-primary mb-2">2B-8B</div>
                    <p className="text-fg/60 text-sm">{t('guardianTech1')}</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary mb-2">Q4_K_M</div>
                    <p className="text-fg/60 text-sm">{t('guardianTechQuant')}</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary mb-2">8GB</div>
                    <p className="text-fg/60 text-sm">{t('guardianTechRam')}</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-primary mb-2">0</div>
                    <p className="text-fg/60 text-sm">{t('guardianTech3')}</p>
                  </div>
                </div>
              </div>
            </section>
          </FadeInSection>

          {/* CTA Section */}
          <FadeInSection delay={600}>
            <section className="container mx-auto px-6 py-16 mb-12">
              <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 md:p-12 text-center text-bg shadow-xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {t('guardianCtaTitle')}
                </h2>
                <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                  {t('guardianCtaSubtitle')}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="mailto:khamit@combi.kz" className="flex items-center gap-2 px-6 py-3 bg-bg backdrop-blur-sm text-fg rounded-xl hover:shadow-lg transition hover:scale-105 font-semibold">
                    <Mail size={20} /> khamit@combi.kz
                  </a>
                  <a href="tel:+77712292371" className="flex items-center gap-2 px-6 py-3 bg-fg/20 rounded-xl hover:bg-fg/30 transition hover:scale-105 backdrop-blur-sm">
                    <Phone size={20} /> +7 771 229 2371
                  </a>
                  <a href="https://t.me/lordekz" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-6 py-3 bg-fg/20 rounded-xl hover:bg-fg/30 transition hover:scale-105 backdrop-blur-sm">
                    <MessageSquare size={20} /> Telegram
                  </a>
                </div>
                <p className="text-sm mt-6 opacity-75">
                  {t('guardianCtaFooter')}
                </p>
              </div>
            </section>
          </FadeInSection>

          {/* ✅ ВСТАВЬТЕ РЕКЛАМУ ЗДЕСЬ: перед футером */}
          <AdBanner />

        </main>

        <Footer />

        <RequestModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
        />
      </div>
    </>
  );
};

export default MaryGuardianPage;